// Sortable data table with an Export CSV button for the Skip report pages.
//
// Reuses the report module's formatCell / buildCsv / downloadCsv helpers so
// the rendered values and the exported file match the rest of the Reports
// module exactly. Sorting is column-local: click a sortable header to toggle
// ascending / descending; numeric column types sort numerically.

import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Download } from 'lucide-react';
import { Button } from '../../../../components/ui/Button/Button';
import {
  buildCsv,
  downloadCsv,
  formatCell,
  todayIsoDate,
} from '../../reportRunnerUtils';
import type { ReportCell, ReportColumn, ReportColumnType } from '../../useReports';
import styles from '../skipReports.module.css';

export interface SortableColumn extends ReportColumn {
  /** Defaults to true. Set false to lock a column from sorting. */
  sortable?: boolean;
}

interface SortableTableProps {
  columns: SortableColumn[];
  rows: Record<string, ReportCell>[];
  /** CSV filename stem; the export appends `-YYYY-MM-DD.csv`. */
  exportName: string;
  emptyMessage?: string;
}

const NUMERIC_TYPES: ReadonlySet<ReportColumnType> = new Set([
  'number',
  'currency',
  'percent',
]);

export function SortableTable({
  columns,
  rows,
  exportName,
  emptyMessage = 'No data for this period.',
}: SortableTableProps): JSX.Element {
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);

  const sortedRows = useMemo(() => {
    if (sort === null) return rows;
    const col = columns.find((c) => c.key === sort.key);
    const numeric = col ? NUMERIC_TYPES.has(col.type) : false;
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sort.key] ?? null;
      const bv = b[sort.key] ?? null;
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      if (numeric) return (Number(av) - Number(bv)) * factor;
      return String(av).localeCompare(String(bv)) * factor;
    });
  }, [rows, columns, sort]);

  function toggleSort(key: string): void {
    setSort((prev) => {
      if (prev === null || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  }

  function handleExport(): void {
    const csv = buildCsv({ columns, rows: sortedRows });
    downloadCsv(`${exportName}-${todayIsoDate()}.csv`, csv);
  }

  return (
    <div>
      <div className={styles.tableToolbar}>
        <Button
          variant="ghost"
          icon={Download}
          onClick={handleExport}
          disabled={rows.length === 0}
        >
          Export CSV
        </Button>
      </div>
      {rows.length === 0 ? (
        <p className={styles.empty}>{emptyMessage}</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                {columns.map((c) => {
                  const sortable = c.sortable !== false;
                  const active = sort?.key === c.key;
                  return (
                    <th
                      key={c.key}
                      className={`${styles[`col_${c.type}`] ?? ''} ${
                        sortable ? styles.sortable : ''
                      }`}
                      onClick={sortable ? () => toggleSort(c.key) : undefined}
                      aria-sort={
                        active ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : undefined
                      }
                    >
                      {c.label}
                      {active ? (
                        <span className={styles.sortIndicator} aria-hidden>
                          {sort?.dir === 'asc' ? (
                            <ArrowUp size={12} />
                          ) : (
                            <ArrowDown size={12} />
                          )}
                        </span>
                      ) : null}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, idx) => (
                <tr key={idx}>
                  {columns.map((c) => (
                    <td key={c.key} className={styles[`col_${c.type}`] ?? ''}>
                      {formatCell(row[c.key] ?? null, c.type)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
